import type { MachinePoolEntry } from "@afar-research/afar";
import { EnvironmentId } from "@afar-research/contracts";
import { useEffect, useRef, useState } from "react";

import { machinePool } from "../connection/machinePool";
import { readLocalApi } from "../localApi";
import { useAtomCommand } from "../state/use-atom-command";

const EMPTY: ReadonlyArray<MachinePoolEntry> = [];

/**
 * The Machine Pool of one project, read once per project (ADR-0149). Null until it has been read,
 * so a placement waits for it rather than deciding without it; empty where AFAR Networks are not
 * offered or the project has none, which leaves placement as it was.
 */
export function useMachinePool(
  environmentId: string | null,
  projectId: string | null,
): ReadonlyArray<MachinePoolEntry> | null {
  const list = useAtomCommand(machinePool.list, { reportFailure: false });
  // Read through a ref: the pool is read once per project, never again because the command's
  // identity changed, which would re-read it on every render.
  const listRef = useRef(list);
  listRef.current = list;
  const [entries, setEntries] = useState<ReadonlyArray<MachinePoolEntry> | null>(null);
  const offered = readLocalApi()?.connect?.networks !== undefined;
  useEffect(() => {
    if (!offered || environmentId === null || projectId === null) {
      setEntries(EMPTY);
      return;
    }
    let cancelled = false;
    setEntries(null);
    void listRef
      .current({ environmentId: EnvironmentId.make(environmentId), projectId })
      .then((result) => {
        if (!cancelled) setEntries(result._tag === "Success" ? result.value : EMPTY);
      });
    return () => {
      cancelled = true;
    };
  }, [offered, environmentId, projectId]);
  return entries;
}
